/*
 * We're always duplicating this code, make it as a clear API.
 */

type ComparisonFunction<T> = (v1: T, v2: T) => number;

/**
 * Compare values and put undefined|null values first.
 */
function undefinedFirst<T>(
    compare: ComparisonFunction<T> = primitive
): ComparisonFunction<T | undefined | null> {
    return (v1: T | undefined | null, v2: T | undefined | null) => {
        const v1Defined = v1 !== undefined && v1 !== null;
        const v2Defined = v2 !== undefined && v2 !== null;
        if (v1Defined && v2Defined) {
            return compare(v1, v2);
        } else if (v1Defined) {
            return 1;
        } else if (v2Defined) {
            return -1;
        } else {
            return 0;
        }
    };
}

/**
 * Compare values and put undefined|null values last.
 */
function undefinedLast<T>(
    compare: ComparisonFunction<T> = primitive
): ComparisonFunction<T | undefined | null> {
    return (v1: T | undefined | null, v2: T | undefined | null) => {
        const v1Defined = v1 !== undefined && v1 !== null;
        const v2Defined = v2 !== undefined && v2 !== null;
        if (v1Defined && v2Defined) {
            return compare(v1, v2);
        } else if (v1Defined) {
            return -1;
        } else if (v2Defined) {
            return 1;
        } else {
            return 0;
        }
    };
}

type Emptyable = string | Array<string>;

/**
 * Compare values and put empty values first.
 */
function emptyFirst(
    compare: ComparisonFunction<Emptyable> = primitive
): ComparisonFunction<Emptyable> {
    return (v1: Emptyable, v2: Emptyable) => {
        const v1NotEmpty = v1.length !== 0;
        const v2NotEmpty = v2.length !== 0;
        if (v1NotEmpty && v2NotEmpty) {
            return compare(v1, v2);
        } else if (v1NotEmpty) {
            return 1;
        } else if (v2NotEmpty) {
            return -1;
        } else {
            return 0;
        }
    };
}

/**
 * Compare values and put empty values last.
 */
function emptyLast(
    compare: ComparisonFunction<Emptyable> = primitive
): ComparisonFunction<Emptyable> {
    return (v1: Emptyable, v2: Emptyable) => {
        const v1NotEmpty = v1.length !== 0;
        const v2NotEmpty = v2.length !== 0;
        if (v1NotEmpty && v2NotEmpty) {
            return compare(v1, v2);
        } else if (v1NotEmpty) {
            return -1;
        } else if (v2NotEmpty) {
            return 1;
        } else {
            return 0;
        }
    };
}

/**
 * Uses < and > operators
 */
function primitive<T>(v1: T, v2: T) {
    if (v1 < v2) {
        return -1;
    } else if (v1 > v2) {
        return 1;
    } else {
        return 0;
    }
}

/**
 * Build an Intl.Collator and expose the compare function
 * Performance in loops & sorts should be greater that localeCompare
 */
function string(): ComparisonFunction<string> {
    const collator = new Intl.Collator();
    return collator.compare;
}

/**
 * Compare timestamps, because comparing dates doesn't work in javascript.
 */
const date = map<Date, number>((v) => v.getTime());

/**
 * Maps a value to another.
 */
function map<T, U>(
    mapper: (v: T) => U,
    compare: ComparisonFunction<U> = primitive
): ComparisonFunction<T> {
    return (v1: T, v2: T) => {
        const u1 = mapper(v1);
        const u2 = mapper(v2);
        return compare(u1, u2);
    };
}

/**
 * Chained comparison
 */
function chain<T>(
    compares: Array<ComparisonFunction<T>>
): ComparisonFunction<T> {
    return (v1: T, v2: T): number => {
        let cmp = 0;
        for (const func of compares) {
            cmp = func(v1, v2);
            if (cmp) {
                break;
            }
        }
        return cmp;
    };
}

function inverse<T>(compare: ComparisonFunction<T>): ComparisonFunction<T> {
    return (v1: T, v2: T) => {
        return -1 * compare(v1, v2);
    };
}

function arrayCompare<T>(
    items1: Array<T>,
    items2: Array<T>,
    itemCompare: (item1: T, item2: T) => number
): number {
    let i = 0;
    while (true) {
        if (i >= items1.length && i >= items2.length) {
            return 0;
        }
        if (i >= items1.length) {
            return 1;
        }
        if (i >= items2.length) {
            return -1;
        }
        const comp = itemCompare(items1[i], items2[i]);
        if (comp !== 0) {
            return comp;
        }
        i++;
    }
}

/**
 * Perform a case-insensitive equality check that matches the locale defined in database (en_us.utf8)
 */
function citextEq(a: string, b: string): boolean {
    return a.localeCompare(b, "en-US", { sensitivity: "accent" }) === 0;
}

export {
    type ComparisonFunction,
    undefinedFirst,
    undefinedLast,
    emptyFirst,
    emptyLast,
    primitive,
    string,
    date,
    map,
    chain,
    inverse,
    arrayCompare,
    citextEq,
};
